import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarCheck, ClipboardCheck, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/track";
import SEO from "@/components/SEO";

const inputCls = "w-full min-h-[52px] rounded-xl border border-sand bg-white px-4 text-base text-navy placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      track("contact_submitted");
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-[80vh]">
      <SEO title="Contact Us" description="Questions about the Legacy Checkup, your report, or scheduling a complimentary Legacy Review? Contact Legacy Wave Consulting." path="/contact" />
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold mb-4">Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-navy leading-tight">
            We're here to help — gently
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-xl">
            Questions about the Legacy Checkup™, your report, or scheduling a conversation? Send us a note below,
            email <a href="mailto:legacy@legacywaveconsulting.com" className="font-semibold text-navy underline decoration-gold/60 underline-offset-2">legacy@legacywaveconsulting.com</a>,
            or call <a href="tel:+17708686372" className="font-semibold text-navy underline decoration-gold/60 underline-offset-2">(770) 868-6372</a>.
          </p>
        </Reveal>

        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <Reveal delay={0.05}>
            <Link to="/checkup" className="block h-full bg-white rounded-3xl border border-sand p-6 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid="contact-checkup-card">
              <ClipboardCheck className="w-7 h-7 text-gold" />
              <p className="mt-3 font-serif text-xl font-semibold text-navy">Take the Legacy Checkup™</p>
              <p className="mt-1 text-sm text-slate-600">Three minutes to personalized clarity.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-gold">Begin <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/book" className="block h-full bg-white rounded-3xl border border-sand p-6 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid="contact-book-card">
              <CalendarCheck className="w-7 h-7 text-gold" />
              <p className="mt-3 font-serif text-xl font-semibold text-navy">Schedule a Legacy Review</p>
              <p className="mt-1 text-sm text-slate-600">A complimentary 30-minute conversation.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-gold">Book now <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-sand p-7 sm:p-8 space-y-5" data-testid="contact-form">
            <h2 className="font-serif text-2xl font-semibold text-navy">Send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="c-name">Name</label>
                <input id="c-name" required data-testid="contact-name-input" className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="c-email">Email</label>
                <input id="c-email" type="email" required data-testid="contact-email-input" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="c-msg">Message</label>
              <textarea id="c-msg" required rows={5} data-testid="contact-message-input" className={`${inputCls} py-3 min-h-[120px]`} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit-button" className="min-h-[56px] rounded-full bg-navy hover:bg-navy-light text-cream font-extrabold text-sm tracking-widest px-8 transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2">
              <Send className="w-4 h-4" /> {loading ? "SENDING…" : "SEND MESSAGE"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
